<template>
  <LoadingDiv v-if="!isReady" />

  <div v-else class="system-module-shell">
    <div class="system-module-toolbar">
      <div>
        <span class="system-module-eyebrow">Global Availability</span>
        <h5>System Modules</h5>
        <p>
          Select the modules this installation can use. User access can only be granted from active modules.
        </p>
      </div>
      <span class="system-module-count">{{ activeCount }} active</span>
    </div>

    <div class="system-module-error" v-if="error">
      {{ error }}
    </div>

    <section
      v-for="group in moduleGroups"
      :key="group.type"
      class="system-module-group"
    >
      <div class="system-module-group-head">
        <div>
          <span class="system-module-eyebrow">{{ group.type }}</span>
          <h6>{{ groupTitle(group.type) }}</h6>
        </div>
        <span>{{ group.modules.filter((module) => module.active).length }}/{{ group.modules.length }} active</span>
      </div>

      <div class="system-module-list">
        <article
          v-for="module in group.modules"
          :key="module.id"
          class="system-module-row"
          :class="{ 'is-inactive': !module.active }"
        >
          <i class="material-icons">{{ module.icon || "apps" }}</i>
          <div>
            <strong>{{ module.title }}</strong>
            <small>{{ module.description }}</small>
          </div>
          <button
            type="button"
            class="system-module-toggle"
            :class="{ 'is-active': module.active }"
            :disabled="isLoading === module.id"
            @click="toggleModule(module)"
          >
            <span>{{ isLoading === module.id ? "Saving" : module.active ? "Active" : "Inactive" }}</span>
            <i class="material-icons">{{ module.active ? "toggle_on" : "toggle_off" }}</i>
          </button>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import getItems from "@/composables/getItems";
import putItem from "@/composables/putItem";
import LoadingDiv from "@/components/LoadingDiv.vue";

export default {
  name: "SystemModuleSetupComponent",
  components: { LoadingDiv },
  emits: ["next"],
  setup() {
    const isReady = ref(false);
    const isLoading = ref(null);
    const error = ref(null);
    const modules = ref([]);

    const moduleGroups = computed(() => {
      const groups = modules.value.reduce((carry, module) => {
        if (!carry[module.type]) {
          carry[module.type] = [];
        }

        carry[module.type].push(module);
        return carry;
      }, {});

      return Object.keys(groups).map((type) => ({
        type,
        modules: groups[type],
      }));
    });

    const activeCount = computed(() =>
      modules.value.filter((module) => module.active).length
    );

    const groupTitle = (type) => {
      const titles = {
        hmo: "HMO Staff Modules",
        provider: "Provider Modules",
        client: "Client Modules",
        principal: "Principal Modules",
      };

      return titles[type] || `${type} Modules`;
    };

    const loadModules = async () => {
      error.value = null;
      const { res, err } = await getItems("system/modules");

      if (res.value) {
        modules.value = res.value.data || [];
      } else {
        error.value = err.value || "Unable to load system modules.";
      }

      isReady.value = true;
    };

    const toggleModule = async (module) => {
      isLoading.value = module.id;
      error.value = null;

      const { res, err } = await putItem("system/modules", module.id, {
        active: !module.active,
      });

      if (res.value) {
        modules.value = modules.value.map((item) =>
          item.id === module.id ? res.value.data : item
        );
      } else {
        error.value = err.value || "Unable to update system module.";
      }

      isLoading.value = null;
    };

    onMounted(() => {
      loadModules();
    });

    return {
      isReady,
      isLoading,
      error,
      modules,
      moduleGroups,
      activeCount,
      groupTitle,
      toggleModule,
    };
  },
};
</script>

<style scoped>
.system-module-shell {
  display: grid;
  gap: 16px;
}

.system-module-toolbar,
.system-module-group {
  background: #ffffff;
  border: 1px solid #e3e8eb;
  border-radius: 6px;
  box-shadow: 0 16px 28px -30px rgba(38, 50, 56, 0.72);
}

.system-module-toolbar {
  align-items: center;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  padding: 22px 24px;
}

.system-module-eyebrow {
  color: #00897b;
  display: block;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0;
  margin-bottom: 5px;
  text-transform: uppercase;
}

.system-module-toolbar h5,
.system-module-group-head h6 {
  color: #263238;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0;
}

.system-module-toolbar h5 {
  font-size: 1.34rem;
}

.system-module-toolbar p {
  color: #607d8b;
  margin: 6px 0 0;
  max-width: 660px;
}

.system-module-count,
.system-module-group-head span,
.system-module-toggle {
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 800;
  white-space: nowrap;
}

.system-module-count {
  background: #e0f2f1;
  color: #00695c;
  padding: 8px 14px;
}

.system-module-group {
  overflow: hidden;
}

.system-module-group-head {
  align-items: center;
  background: #f8fafb;
  border-bottom: 1px solid #e3e8eb;
  display: flex;
  justify-content: space-between;
  padding: 18px 22px;
}

.system-module-group-head span {
  background: #ffffff;
  border: 1px solid #d8e0e4;
  color: #455a64;
  padding: 7px 12px;
}

.system-module-list {
  display: grid;
}

.system-module-row {
  align-items: center;
  border-bottom: 1px solid #edf1f3;
  display: grid;
  gap: 14px;
  grid-template-columns: 38px 1fr auto;
  padding: 16px 20px;
}

.system-module-row:last-child {
  border-bottom: 0;
}

.system-module-row > i {
  align-items: center;
  background: #eef7f6;
  border: 1px solid #cfe6e2;
  border-radius: 6px;
  color: #00796b;
  display: flex;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.system-module-row strong {
  color: #263238;
  display: block;
  font-size: 1rem;
  line-height: 1.3;
}

.system-module-row small {
  color: #607d8b;
  display: block;
  line-height: 1.45;
  margin-top: 3px;
}

.system-module-row.is-inactive {
  background: #fbfcfc;
}

.system-module-row.is-inactive > i {
  background: #f1f3f4;
  border-color: #e0e5e8;
  color: #90a4ae;
}

.system-module-row.is-inactive strong,
.system-module-row.is-inactive small {
  color: #90a4ae;
}

.system-module-toggle {
  align-items: center;
  background: #eceff1;
  border: 1px solid #cfd8dc;
  color: #607d8b;
  cursor: pointer;
  display: inline-flex;
  gap: 8px;
  justify-content: center;
  min-width: 118px;
  padding: 7px 10px 7px 14px;
}

.system-module-toggle.is-active {
  background: #e0f7ef;
  border-color: #2e7d68;
  color: #00695c;
}

.system-module-toggle:disabled {
  cursor: wait;
  opacity: 0.68;
}

.system-module-toggle i {
  font-size: 1.45rem;
}

.system-module-error {
  background: #fff4f4;
  border: 1px solid #ffcdd2;
  border-radius: 6px;
  color: #b71c1c;
  font-weight: 700;
  padding: 12px 14px;
}

@media (max-width: 720px) {
  .system-module-toolbar,
  .system-module-group-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .system-module-row {
    grid-template-columns: 38px 1fr;
  }

  .system-module-toggle {
    grid-column: 1 / -1;
    justify-self: start;
  }
}
</style>
